/**
 * Two-finger pinch-zoom and pan for the canvas.
 *
 * svgcanvas's own touch support (core/touch.js) turns ONE finger into mouse events and swallows every
 * touch on the canvas with preventDefault(). That is enough to draw with a finger, but it leaves a
 * phone with no way to zoom or to move around a zoomed canvas: the page itself cannot be pinched
 * either (the app's viewport meta forbids it), and the browser's own scrolling never sees a touch the
 * canvas has taken.
 *
 * This listens in the CAPTURE phase on #workarea — an ancestor of the canvas, so it runs before
 * touch.js — and acts only when a second finger arrives. One finger is left entirely to touch.js.
 *
 * The zoom itself is the editor's own: the same calls the shift+wheel handler in svgcanvas makes
 * (setZoom, then the updateCanvas and zoomDone events), so the zoom box, the scroll position and the
 * rulers stay consistent with whatever the user does next.
 */

const MIN_ZOOM = 0.1
const MAX_ZOOM = 10

// A tap is a touch that did not travel; a double-tap is two of them close in time and place.
const TAP_SLOP = 12
const DOUBLE_TAP_MS = 400
const DOUBLE_TAP_DISTANCE = 36

const distance = (a, b) => Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY)
const centre = (a, b) => ({ x: (a.clientX + b.clientX) / 2, y: (a.clientY + b.clientY) / 2 })

/**
 * @param {module:Editor} editor the svgEditor instance (has svgCanvas and workarea)
 * @returns {void}
 */
export const initTouchGestures = (editor) => {
  const { workarea, svgCanvas } = editor

  // The one finger that touch.js already turned into a mousedown, and where it started.
  let single = null
  // Set from the second finger's arrival until every finger is up.
  let gesture = null
  let pending = null
  let frame = 0

  const mouse = (type, x, y, target) => {
    target.dispatchEvent(new MouseEvent(type, {
      bubbles: true, cancelable: true, view: window, detail: 1, button: 0, clientX: x, clientY: y
    }))
  }

  /**
   * The first finger has already told the canvas "mouse down" by the time the second one lands.
   * Left like that the canvas believes a drag is still in progress, and its shape — a half-drawn
   * rectangle, a dragged object — ends wherever the pinch happens to finish. Put the pointer back
   * where the finger started and release it there: zero movement, nothing drawn, nothing moved.
   */
  const cancelSingle = () => {
    if (!single) return
    mouse('mousemove', single.x, single.y, single.target)
    mouse('mouseup', single.x, single.y, single.target)
    single = null
  }

  const contentCtm = () => document.getElementById('svgcontent').querySelector('g').getScreenCTM().inverse()

  // Where, in the drawing's own coordinates, a point on the screen is.
  const toContent = (x, y, inverse) => new DOMPoint(x, y).matrixTransform(inverse)

  const begin = (touches) => {
    const [a, b] = [touches[0], touches[1]]
    const c = centre(a, b)
    gesture = {
      startDistance: Math.max(distance(a, b), 1),
      startZoom: svgCanvas.getZoom(),
      // The point of the drawing under the fingers' midpoint. It is held there for the whole gesture,
      // which is what makes both the zoom (about the fingers) and the pan (the midpoint moving) work.
      anchor: toContent(c.x, c.y, contentCtm())
    }
  }

  const apply = () => {
    frame = 0
    if (!gesture || !pending) return
    const { c, d } = pending
    pending = null

    const zoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, gesture.startZoom * (d / gesture.startDistance)))
    const box = workarea.getBoundingClientRect()

    // Content coordinates of the work area's top-left corner such that the anchor sits under the
    // fingers' midpoint at the new zoom — then the view's centre, in zoomed canvas pixels, which is
    // what the updateCanvas event takes.
    const topLeft = {
      x: gesture.anchor.x - (c.x - box.left) / zoom,
      y: gesture.anchor.y - (c.y - box.top) / zoom
    }
    const newCtr = {
      x: topLeft.x * zoom + box.width / 2,
      y: topLeft.y * zoom + box.height / 2
    }

    svgCanvas.setZoom(zoom)
    const zoomBox = document.getElementById('zoom')
    if (zoomBox) zoomBox.value = (zoom * 100).toFixed(1)
    svgCanvas.call('updateCanvas', { center: false, newCtr })
    svgCanvas.call('zoomDone')
  }

  workarea.addEventListener('touchstart', (e) => {
    if (e.touches.length === 1) {
      // A fresh touch. Whatever gesture was in progress cannot still be (a lost touchend would
      // otherwise leave every later touch swallowed).
      gesture = null
      pending = null
      single = { x: e.touches[0].clientX, y: e.touches[0].clientY, target: e.target }
      return // one finger: touch.js's business
    }

    if (!gesture) {
      cancelSingle()
      begin(e.touches)
    }
    e.preventDefault()
    e.stopPropagation()
  }, { capture: true, passive: false })

  workarea.addEventListener('touchmove', (e) => {
    if (!gesture) return
    e.preventDefault()
    e.stopPropagation()
    if (e.touches.length < 2) return // one finger left over from the gesture: ignore it

    pending = { c: centre(e.touches[0], e.touches[1]), d: distance(e.touches[0], e.touches[1]) }
    if (!frame) frame = requestAnimationFrame(apply)
  }, { capture: true, passive: false })

  const end = (e) => {
    if (gesture) {
      e.preventDefault()
      e.stopPropagation()
      if (e.touches.length === 0) {
        gesture = null
        pending = null
      }
      return
    }
    if (e.touches.length === 0) {
      if (e.type === 'touchend' && single) reportTap(e.changedTouches[0])
      single = null
    }
  }

  /**
   * A double-tap is the touch screen's double-click, and the editor does its in-place editing on
   * double-click: a text opens for editing, a group is entered. svgcanvas's touch layer sends mouse down,
   * move and up but never `dblclick`, so none of that worked on a phone. Two quick taps in nearly the same
   * place make one here.
   */
  let lastTap = null
  const reportTap = (touch) => {
    if (!touch) return
    if (Math.hypot(touch.clientX - single.x, touch.clientY - single.y) > TAP_SLOP) return // it was a drag

    const now = performance.now()
    const target = single.target
    const second = lastTap &&
      now - lastTap.time < DOUBLE_TAP_MS &&
      Math.hypot(touch.clientX - lastTap.x, touch.clientY - lastTap.y) < DOUBLE_TAP_DISTANCE
    lastTap = second ? null : { time: now, x: touch.clientX, y: touch.clientY }
    if (!second) return

    // After this tap's own mouse-up, which touch.js sends from its handler on the canvas, a moment later.
    setTimeout(() => {
      target.dispatchEvent(new MouseEvent('dblclick', {
        bubbles: true, cancelable: true, view: window, detail: 2, button: 0,
        clientX: touch.clientX, clientY: touch.clientY
      }))
    }, 0)
  }
  workarea.addEventListener('touchend', end, { capture: true, passive: false })
  workarea.addEventListener('touchcancel', end, { capture: true, passive: false })
}
